import { Value } from 'api';
import logger from 'utilities/logger';
import { WaterfallRowConfigProps } from './WaterfallRowConfig.props';
import { contentClass, headerClassRight, totalClass } from '../../constants';

const WaterfallRowConfig = ({ scenarioObject, validSecurities, companyName }: WaterfallRowConfigProps) => {
  const { aggregate_values: aggregateValues, present_values: presentValues } = scenarioObject;

  // If no values are available, return an empty configuration to prevent crashes
  if (!aggregateValues || !presentValues || aggregateValues.length === 0 || presentValues.length === 0) {
    logger.debug('No waterfall values available, returning empty configuration');
    return [
      {
        readOnly: true,
        className: headerClassRight,
        largeHeader: true,
      },
      {
        value: 'No waterfall data available',
        readOnly: true,
        className: contentClass,
      },
      {
        value: `${companyName} Totals`,
        readOnly: true,
        className: totalClass,
      },
    ];
  }

  const sortedAggregateValues: Value[] = [];
  const sortedPresentValues: Value[] = [];

  // map validSecurities to the aggregateValues and presentValues, sort them based on the validSecurities order, and then map them back to share values
  validSecurities.forEach(security => {
    const aggregateValue = aggregateValues.find(av => av.security?.id === security.id);
    const presentValue = presentValues.find(pv => pv.security?.id === security.id);

    if (!aggregateValue) {
      const errorMessage = `No aggregate value found for security: ${security.name} (ID: ${security.id}).`;
      logger.error('Warning getting aggregate value for waterfall', errorMessage);

      const defaultAggregateValue: Value = {
        security: { id: security.id, name: security.name },
        value: '0',
      };
      sortedAggregateValues.push(defaultAggregateValue);
    } else {
      sortedAggregateValues.push(aggregateValue);
    }

    if (!presentValue) {
      const warningMessage = `No present value found for security: ${security.name} (ID: ${security.id}).`;
      logger.error('Warning getting present value for waterfall', warningMessage);
      
      // Create a default present value with 0 to prevent crashes
      const defaultPresentValue: Value = {
        security: { id: security.id, name: security.name },
        value: '0',
      };
      sortedPresentValues.push(defaultPresentValue);
    } else {
      sortedPresentValues.push(presentValue);
    }
  });

  return [
    {
      readOnly: true,
      className: headerClassRight,
      largeHeader: true,
    },
    ...sortedAggregateValues.map((aggregateValue, index) => {
      const presentValue = sortedPresentValues[index];
      return {
        securityName: aggregateValue.security?.name,
        aggregateValue: aggregateValue.value,
        presentValue: presentValue?.value,
        readOnly: true,
        className: contentClass,
      };
    }),
    {
      value: `${companyName} Totals`,
      readOnly: true,
      className: totalClass,
    },
  ];
};

export default WaterfallRowConfig;
